import { useMemo } from 'react';
import { Shield, AlertTriangle, AlertCircle, Info, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface VulnerabilityCount {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

interface VulnerabilitySummaryProps {
  counts: VulnerabilityCount;
  onCategoryClick?: (category: RiskLevel) => void;
}

const VulnerabilitySummary = ({ counts, onCategoryClick }: VulnerabilitySummaryProps) => {
  const total = counts.critical + counts.high + counts.medium + counts.low + counts.info;

  const items = [
    {
      key: 'critical' as RiskLevel,
      label: 'Critical',
      count: counts.critical,
      icon: Shield,
      bgClass: 'bg-risk-critical-bg',
      textClass: 'text-risk-critical',
      barClass: 'bg-risk-critical',
    },
    {
      key: 'high' as RiskLevel,
      label: 'High',
      count: counts.high,
      icon: AlertTriangle,
      bgClass: 'bg-risk-high-bg',
      textClass: 'text-risk-high',
      barClass: 'bg-risk-high',
    },
    {
      key: 'medium' as RiskLevel,
      label: 'Medium',
      count: counts.medium,
      icon: AlertCircle,
      bgClass: 'bg-risk-medium-bg',
      textClass: 'text-risk-medium',
      barClass: 'bg-risk-medium',
    },
    {
      key: 'low' as RiskLevel,
      label: 'Low',
      count: counts.low,
      icon: CheckCircle,
      bgClass: 'bg-risk-low-bg',
      textClass: 'text-risk-low',
      barClass: 'bg-risk-low',
    },
    {
      key: 'info' as RiskLevel,
      label: 'Informational',
      count: counts.info,
      icon: Info,
      bgClass: 'bg-risk-info-bg',
      textClass: 'text-risk-info',
      barClass: 'bg-risk-info',
    },
  ];

  const handleClick = (category: RiskLevel) => {
    if (onCategoryClick && counts[category] > 0) {
      onCategoryClick(category);
    }
  };

  // GRTTL4C-122: 100-cell waffle grid — memoize on `counts` to avoid rebuilding on every render
  const waffleChart = useMemo(() => {
            // Build a 10x10 waffle chart (100 cells) proportionally colored
            const gridSize = 100;
            const cells: { key: RiskLevel; color: string; hoverColor: string }[] = [];
            const severities: { key: RiskLevel; count: number; color: string; hoverColor: string }[] = [
              { key: 'critical', count: counts.critical, color: 'bg-red-600', hoverColor: 'hover:bg-red-500' },
              { key: 'high', count: counts.high, color: 'bg-orange-500', hoverColor: 'hover:bg-orange-400' },
              { key: 'medium', count: counts.medium, color: 'bg-yellow-500', hoverColor: 'hover:bg-yellow-400' },
              { key: 'low', count: counts.low, color: 'bg-emerald-500', hoverColor: 'hover:bg-emerald-400' },
              { key: 'info', count: counts.info, color: 'bg-sky-500', hoverColor: 'hover:bg-sky-400' },
            ];

            // Distribute cells proportionally
            let remaining = gridSize;
            severities.forEach((sev, idx) => {
              const isLast = idx === severities.length - 1;
              const proportion = total > 0 ? sev.count / total : 0;
              const cellCount = isLast
                ? remaining
                : Math.round(proportion * gridSize);
              const actualCount = Math.min(cellCount, remaining);
              for (let i = 0; i < actualCount; i++) {
                cells.push({ key: sev.key, color: sev.color, hoverColor: sev.hoverColor });
              }
              remaining -= actualCount;
            });

            // Trim to exact grid size (in case rounding added extra)
            const finalCells = cells.slice(0, gridSize);

            return (
              <div>
                <div className="grid grid-cols-10 gap-[2px] max-w-[200px]">
                  {finalCells.map((cell, i) => (
                    <div
                      key={i}
                      className={cn(
                        'aspect-square rounded-sm transition-all duration-150',
                        cell.color,
                        onCategoryClick && cn('cursor-pointer', cell.hoverColor, 'hover:scale-110')
                      )}
                      onClick={() => onCategoryClick?.(cell.key)}
                      title={`${cell.key} (${counts[cell.key]})`}
                    />
                  ))}
                </div>
                <div className="flex flex-wrap gap-4 mt-4 text-xs text-zinc-400">
                  {severities.filter(s => s.count > 0).map((sev) => (
                    <div key={sev.key} className="flex items-center gap-1.5">
                      <div className={cn('w-3 h-3 rounded-sm', sev.color)} />
                      <span className="capitalize">{sev.key}</span>
                      <span className="text-zinc-500">
                        ({sev.count} · {total > 0 ? Math.round((sev.count / total) * 100) : 0}%)
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Each square represents ~{total > 0 ? (total / gridSize).toFixed(1) : 0} finding{total > gridSize ? 's' : ''}
                </p>
              </div>
            );
  }, [counts, total, onCategoryClick]);

  return (
    <div className="card-elevated p-6 animate-slide-up">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold">Vulnerability Summary</h3>
        <div className="flex items-center gap-2 bg-muted px-4 py-2 rounded-full">
          <span className="text-muted-foreground text-sm">Total Found:</span>
          <span className="font-bold text-xl">{total}</span>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Click on any category below to view detailed vulnerabilities
      </p>

      <div className="space-y-4">
        {items.map((item) => {
          const percentage = total > 0 ? (item.count / total) * 100 : 0;
          const Icon = item.icon;
          const isClickable = item.count > 0 && !!onCategoryClick;

          return (
            <div
              key={item.key}
              className={cn(
                'flex items-center gap-4 p-2 rounded-lg transition-all',
                isClickable && 'cursor-pointer hover:bg-muted/50 hover:translate-x-1'
              )}
              onClick={() => handleClick(item.key)}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onKeyDown={
                isClickable ? (e) => e.key === 'Enter' && handleClick(item.key) : undefined
              }
            >
              <div className={`${item.bgClass} ${item.textClass} p-2 rounded-lg`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className={cn('font-medium text-sm', isClickable && 'underline')}>
                    {item.label}
                  </span>
                  <span className={`font-bold ${item.textClass}`}>{item.count}</span>
                </div>
                <div className="progress-bar">
                  <div
                    className={`progress-bar-fill ${item.barClass}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Heat Map Visualization - Waffle Chart */}
      {total > 0 && (
        <div className="mt-8 pt-6 border-t border-border">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
            Severity Distribution
          </h4>
          {/* GRTTL4C-122: waffle grid memoized — see const waffleChart above */}
          {waffleChart}
        </div>
      )}
    </div>
  );
};

export default VulnerabilitySummary;
